/**
 * StaffPreferencesForm — staff/admin/manager-only profile controls:
 *
 *   - Default home: dropdown that sets users.staff_home_route. "Use the
 *     tenant default" maps to NULL on save (the tenant default takes over).
 *   - Show daily sign-in tips: checkbox bound to users.tips_disabled
 *     (inverted — UI says "show," DB says "disabled").
 *   - My notifications: the staff notices the club sends to the person's
 *     role, each of which they may turn off for themselves only
 *     (MyNotificationSettings, PM 465c33cc).
 *
 * Saves immediately on change via PATCH /users/me/preferences. Updates
 * localStorage.userDetails so the navbar logo reflects the new choice
 * without a full reload.
 *
 * The saved values are read from the server (GET /auth/current-user, which
 * carries staff_home_route and tips_disabled for tenant users and tenant
 * owners alike). The stored userDetails is only the first guess: the
 * profile refresh rewrites it from an endpoint that does not carry these
 * fields for a tenant owner, so "Show daily sign-in tips" reopened ticked
 * after being turned off (2026-10-10).
 */

import React, { useEffect, useRef, useState } from "react";
import { captureError } from "../../utils/sentry";
import { toast } from "react-toastify";
import axios from "../../api/axiosInstance";
import config from "../../api/config";
import { getTenantId, getAuthSource } from "../../api/apiHelpers";
import {
  STAFF_HOME_ROUTE_OPTIONS,
  StaffHomeRoute,
  isStaffHomeRoute,
} from "../../utils/staffHomeRoute";
import MyNotificationSettings from "./MyNotificationSettings";

interface Props {
  isDarkMode: boolean;
}

const readStored = (): Record<string, any> => {
  try { return JSON.parse(localStorage.getItem("userDetails") || "{}") || {}; }
  catch { return {}; }
};

const StaffPreferencesForm: React.FC<Props> = ({ isDarkMode }) => {
  const stored = readStored();

  const [homeRoute, setHomeRoute] = useState<StaffHomeRoute | "">(
    isStaffHomeRoute(stored?.staff_home_route) ? stored.staff_home_route : "",
  );
  const [tenantDefault, setTenantDefault] = useState<StaffHomeRoute | null>(
    isStaffHomeRoute(stored?.tenant_default_home_route) ? stored.tenant_default_home_route : null,
  );
  const [tipsDisabled, setTipsDisabled] = useState<boolean>(!!stored?.tips_disabled);
  const [savingField, setSavingField] = useState<"home" | "tips" | null>(null);
  // Once the person changes something here, a late server read must not
  // put the old value back.
  const changedHere = useRef(false);

  const token = localStorage.getItem("authToken");
  const endpoint = `${config.BASE_URL}/users/me/preferences`;
  const headers = {
    "Content-Type": "application/json",
    "x-tenant-id": getTenantId(),
    "x-auth-source": getAuthSource(),
    authorization: `Bearer ${token}`,
  };

  useEffect(() => {
    let live = true;
    axios
      .get(`${config.BASE_URL}/auth/current-user`, { headers })
      .then((res) => {
        const user = res?.data?.user;
        if (!live || !user || changedHere.current) return;
        setTipsDisabled(!!user.tips_disabled);
        setHomeRoute(isStaffHomeRoute(user.staff_home_route) ? user.staff_home_route : "");
        if (isStaffHomeRoute(user.tenant_default_home_route)) setTenantDefault(user.tenant_default_home_route);
        // Keep the stored copy in step for the navbar and the next visit.
        localStorage.setItem(
          "userDetails",
          JSON.stringify({
            ...readStored(),
            staff_home_route: user.staff_home_route ?? null,
            tips_disabled: !!user.tips_disabled,
            ...(user.tenant_default_home_route ? { tenant_default_home_route: user.tenant_default_home_route } : {}),
          }),
        );
      })
      .catch((err) => {
        // The stored values stay on screen; the next save still goes to the server.
        captureError(err, { component: "StaffPreferencesForm", operation: "readPreferences" }, "warning");
      });
    return () => {
      live = false;
    };
    // Read once when the form opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patchAndStore = async (
    body: { staff_home_route?: StaffHomeRoute | null; tips_disabled?: boolean },
    which: "home" | "tips",
  ) => {
    setSavingField(which);
    changedHere.current = true;
    try {
      const res = await axios.patch(endpoint, body, { headers });
      if (res?.data?.success) {
        // Mirror new value back into localStorage so navbar + redirect see it.
        const next = { ...readStored(), ...res.data.data };
        localStorage.setItem("userDetails", JSON.stringify(next));
        // SideNavbarLayout keeps userDetails in React state and listens for
        // this event — dispatching it makes the logo's home link update
        // immediately instead of waiting for a sign-out/sign-in.
        window.dispatchEvent(new Event("user-details-updated"));
        toast.success("Preferences updated");
      } else {
        toast.error(res?.data?.error || "Failed to save");
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Failed to save");
    } finally {
      setSavingField(null);
    }
  };

  const handleHomeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value;
    if (v === "") {
      setHomeRoute("");
      patchAndStore({ staff_home_route: null }, "home");
    } else if (isStaffHomeRoute(v)) {
      setHomeRoute(v);
      patchAndStore({ staff_home_route: v }, "home");
    }
  };

  const handleTipsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const showTips = e.target.checked;
    setTipsDisabled(!showTips);
    patchAndStore({ tips_disabled: !showTips }, "tips");
  };

  const tenantDefaultLabel = tenantDefault
    ? STAFF_HOME_ROUTE_OPTIONS.find((o) => o.value === tenantDefault)?.label
    : null;

  const selectClass = `w-full px-3 py-2 border rounded-md ${
    isDarkMode ? "bg-gray-700 border-gray-800 text-white" : "bg-white border-gray-300"
  }`;

  return (
    <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
      <div>
        <label className="block text-sm font-medium mb-2">Default home page</label>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Where you land after sign-in and where the logo takes you. Leave on
          the club default unless you have a specific preference.
        </p>
        <select
          value={homeRoute}
          onChange={handleHomeChange}
          disabled={savingField === "home"}
          className={selectClass}
        >
          <option value="">
            Use club default
            {tenantDefaultLabel ? ` (${tenantDefaultLabel})` : ""}
          </option>
          {STAFF_HOME_ROUTE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!tipsDisabled}
            onChange={handleTipsChange}
            disabled={savingField === "tips"}
            className="mt-1"
          />
          <span>
            <span className="block text-sm font-medium">Show daily sign-in tips</span>
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              A small card on the dashboard surfaces a new tip each day — overlooked
              features, recent additions, and best practices. Uncheck to hide.
            </span>
          </span>
        </label>
      </div>

      <MyNotificationSettings />
    </div>
  );
};

export default StaffPreferencesForm;
